<template>
  <!-- fill color -->
  <core-tooltip text="Fill color">
    <u-button @click="openFillPanel" variant="ghost" color="charcoal">
      <div class="relative bg-checkerboard w-5 h-5 rounded-full shadow overflow-hidden">
        <div class="absolute-cover" :style="fillStyle" />
        <div v-if="content.fill?.color === 'transparent'" class="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            class="w-14 h-0.5 bg-red-500 -rotate-45 rounded-full shadow-sm"
          />
        </div>
      </div>
    </u-button>
  </core-tooltip>

  <surface-editor-core-toolbar-divider />

  <core-tooltip v-if="hasStroke" text="Stroke color">
    <u-button @click="openStrokeColorPanel" variant="ghost" color="charcoal">
      <div class="w-5 h-5 rounded-full border-4 shadow" :style="{
        background: '#ffffff',
        borderColor: content.stroke_color
      }" />
    </u-button>
  </core-tooltip>

  <!-- stroke style -->
  <u-popover>
    <core-tooltip text="Stroke style">
      <u-button  color="charcoal" variant="ghost" icon="fluent-line-style-24-filled" class="dark:text-white" />
    </core-tooltip>

    <template #panel>
      <div class="p-4 space-y-4">

        <!-- Stroke Style Selector -->
        <div>
          <label class="text-sm font-semibold mb-1">Stroke Style</label>
          <div class="grid grid-cols-4 gap-2">
            <u-button
              block
              size="lg"
              icon="i-ri-prohibited-line"
              :color="(!strokeType || strokeType === SURFACE_EDITOR_STROKE_STYLES.none) ? 'primary' : 'charcoal'"
              :variant="(!strokeType || strokeType === SURFACE_EDITOR_STROKE_STYLES.none) ? 'solid' : 'outline'"
              class="dark:text-white"
              @click="setStrokeType(SURFACE_EDITOR_STROKE_STYLES.none)"
            />

            <u-button
              block
              size="lg"
              icon="fluent-line-horizontal-1-24-filled"
              :color="strokeType === SURFACE_EDITOR_STROKE_STYLES.solid ? 'primary' : 'charcoal'"
              :variant="strokeType === SURFACE_EDITOR_STROKE_STYLES.solid ? 'solid' : 'outline'"
              class="dark:text-white"
              @click="setStrokeType(SURFACE_EDITOR_STROKE_STYLES.solid)"
            />

            <u-button
              block
              size="lg"
              icon="fluent-line-horizontal-1-dashes-24-filled"
              :color="strokeType === SURFACE_EDITOR_STROKE_STYLES.dashed ? 'primary' : 'charcoal'"
              :variant="strokeType === SURFACE_EDITOR_STROKE_STYLES.dashed ? 'solid' : 'outline'"
              class="dark:text-white"
              @click="setStrokeType(SURFACE_EDITOR_STROKE_STYLES.dashed)"
            />

            <u-button
              block
              size="lg"
              icon="fluent-line-horizontal-1-dot-20-filled"
              :color="strokeType === SURFACE_EDITOR_STROKE_STYLES.dotted ? 'primary' : 'charcoal'"
              :variant="strokeType === SURFACE_EDITOR_STROKE_STYLES.dotted ? 'solid' : 'outline'"
              class="dark:text-white"
              @click="setStrokeType(SURFACE_EDITOR_STROKE_STYLES.dotted)"
            />
          </div>
        </div>

        <surface-editor-core-slider-input
          v-model="strokeWidth"
          @drag-start="surfaceEditorStore.setSliderDragging(true)"
          @drag-end="surfaceEditorStore.setSliderDragging(false)"
          @commit-updates="surfaceEditorStore.commitUndo()"
        >
          <template #label>Stroke Width</template>
        </surface-editor-core-slider-input>

      </div>
    </template>
  </u-popover>

  <!-- corner radius -->
  <u-popover>
    <core-tooltip text="Corner rounding">
      <u-button color="charcoal" variant="ghost" icon="i-ri-rounded-corner" class="dark:text-white" />
    </core-tooltip>
    <template #panel>
      <surface-editor-core-slider-input
        v-model="cornerRounding"
        @drag-start="surfaceEditorStore.setSliderDragging(true)"
        @drag-end="surfaceEditorStore.setSliderDragging(false)"
        @commit-updates="surfaceEditorStore.commitUndo()"
        class="p-3"
      >
        <template #label>Corner Rounding</template>
      </surface-editor-core-slider-input>
    </template>
  </u-popover>

</template>

<script setup>
  import {storeToRefs} from 'pinia';

  const surfaceEditorStore = useSurfaceEditorStore();

  const {selectedElement} = storeToRefs(surfaceEditorStore);
  const content = computed(() => selectedElement.value?.content);

  const surfaceEditorUtils = useSurfaceEditorUtils();
  const fillStyle = computed(() => surfaceEditorUtils.fillToStyle({fill: content.value.fill}));

  const cornerRounding = computed({
    get() {
      return content.value.rounding;
    },
    set(rounding) {
      surfaceEditorStore.updateElement({
        element: selectedElement.value,
        updates: {
          content: {rounding}
        },
        commit: false
      });
    }
  });

  const strokeWidth = computed({
    get() {
      return content.value.stroke_type === SURFACE_EDITOR_STROKE_STYLES.none ? 0 : content.value.stroke_width
    },
    set(strokeWidth) {
      surfaceEditorStore.updateElement({
        element: selectedElement.value,
        updates: {
          content: {
            stroke_width: strokeWidth
          }
        },
        commit: false
      });
    }
  });

  const strokeType = computed(() => (content.value.stroke_width ? content.value.stroke_type : SURFACE_EDITOR_STROKE_STYLES.none));

  function setStrokeType(strokeType) {
    surfaceEditorStore.updateElement({
      element: selectedElement.value,
      updates: {
        content: {
          stroke_type: strokeType
        }
      }
    });

    if (strokeType !== SURFACE_EDITOR_STROKE_STYLES.none && !content.value.stroke_width) {
      strokeWidth.value = 4;
    }
  }

  const hasStroke = computed(() => content.value.stroke_type !== SURFACE_EDITOR_STROKE_STYLES.none  && strokeWidth.value)

  function openFillPanel() {
    surfaceEditorStore.openContextualPanel({
      id: 'color',
      props: {
        current: computed(() => content.value.fill),
        onSelect: setFill,
        mode: SURFACE_EDITOR_COLOR_PANEL_MODES.shapeFill
      }
    });
  }

  function openStrokeColorPanel() {
    surfaceEditorStore.openContextualPanel({
      id: 'color',
      props: {
        current: computed(() => ({
          color: content.value.stroke_color,
          type: SURFACE_EDITOR_COLOR_TYPES.solid.key
        })),
        onSelect: setStroke,
        mode: SURFACE_EDITOR_COLOR_PANEL_MODES.shapeStroke
      }
    });
  }

  function setStroke(selected) {
    surfaceEditorStore.updateElement({
      element: selectedElement.value,
      updates: {
        content: {
          stroke_color: selected.color
        }
      }
    });
  }

  function setFill(selectedFill) {
    const fill = (() => {
      switch (selectedFill.type) {
        case SURFACE_EDITOR_FILL_TYPES.solid:
          return {
            type: SURFACE_EDITOR_FILL_TYPES.solid,
            color: selectedFill.color
          };
        case SURFACE_EDITOR_FILL_TYPES.linearGradient:
          return {
            type: SURFACE_EDITOR_FILL_TYPES.linearGradient,
            angle: selectedFill.angle,
            stops: selectedFill.stops
          };
        case SURFACE_EDITOR_FILL_TYPES.imagePattern:
          return {
            type: SURFACE_EDITOR_FILL_TYPES.imagePattern,
            url: selectedFill.url,
            scale: 1
          };
      }
    })();

    surfaceEditorStore.updateElement({
      element: selectedElement.value,
      updates: {
        content: {
          fill
        }
      }
    });
  }

  onMounted(() => {
    const panel = surfaceEditorStore.contextualPanel;
    if (panel?.id !== 'color') return;

    const fillModes = [SURFACE_EDITOR_COLOR_PANEL_MODES.shapeFill, SURFACE_EDITOR_COLOR_PANEL_MODES.bgFill];
    if (fillModes.includes(panel.props?.mode)) {
      openFillPanel();
    } else if (panel.props?.mode === SURFACE_EDITOR_COLOR_PANEL_MODES.shapeStroke) {
      openStrokeColorPanel();
    }
  });

</script>
